import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Stack,
} from '@mui/material';
import {
  AutoAwesome as ClassifyIcon,
  PlayArrow as RunIcon,
  Replay as ForceIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import apiClient from '../lib/api';
import { toast } from 'react-hot-toast';

/**
 * Categorization management (BR-CAT-09b/c): run the classifier (dry-run / full / force), surface the
 * returned summary (total/classified/skipped/failed + spend), show categorized-vs-uncategorized and
 * per-category counts, and spot-check a single card's assignments + provenance model (mock-vs-real).
 * Admin-only — backed by the gated /api/admin/categorize* + /api/admin/cards/:id/categories endpoints.
 */

const SummaryRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

const CategorizationManager = () => {
  const [running, setRunning] = useState(false);
  const [backgroundRunning, setBackgroundRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const [lastMode, setLastMode] = useState(null);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [cardId, setCardId] = useState('');
  const [cardLoading, setCardLoading] = useState(false);
  const [cardResult, setCardResult] = useState(null);
  const [cardError, setCardError] = useState(null);

  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await apiClient.getCategorizationStats();
      setStats(res?.data || null);
    } catch (err) {
      // Stats are non-critical; surface but don't block the run controls.
      toast.error(err?.response?.data?.error || err.message || 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollCountRef.current = 0;
    setBackgroundRunning(false);
  }, []);

  // A real bulk run happens in the async categorizeRunner Lambda, so the UI polls stats for progress
  // rather than waiting on the request. Cleared on unmount and after a bounded number of polls.
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollCountRef.current = 0;
    setBackgroundRunning(true);
    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      await loadStats();
      if (pollCountRef.current >= MAX_POLLS) stopPolling();
    }, POLL_INTERVAL_MS);
  }, [loadStats, stopPolling]);

  useEffect(() => {
    loadStats();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadStats]);

  const run = async ({ dryRun = false, force = false }) => {
    setRunning(true);
    setError(null);
    const mode = dryRun ? 'Dry run' : force ? 'Force re-run' : 'Run';
    try {
      const res = await apiClient.runCategorization({ dryRun, force });
      const data = res?.data || {};
      setLastMode(mode);

      if (data.async) {
        // Fire-and-forget: the runner is doing the work; reflect progress by polling stats.
        setSummary(null);
        toast.success(data.message || `${mode} started in the background`);
        await loadStats();
        startPolling();
      } else {
        // Synchronous path (dry-run preview, or inline fallback when the runner isn't deployed).
        setSummary(data.summary || null);
        toast.success(`${mode} complete`);
        await loadStats();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Categorization failed');
    } finally {
      setRunning(false);
    }
  };

  const lookupCard = async () => {
    const id = cardId.trim();
    if (!id) return;
    setCardLoading(true);
    setCardError(null);
    setCardResult(null);
    try {
      const res = await apiClient.getCardCategories(id);
      setCardResult(res?.data || null);
    } catch (err) {
      setCardError(err?.response?.data?.error || err.message || 'Lookup failed');
    } finally {
      setCardLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ClassifyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Run categorization
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Classify the card backlog. A <strong>run</strong> classifies only the cards that need it —
            new or stale ones. <strong>Force re-run</strong> re-does every card. Human assignments are
            never overwritten.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Button
              variant="contained"
              startIcon={<RunIcon />}
              onClick={() => run({})}
              disabled={running}
            >
              Run
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ForceIcon />}
              onClick={() => run({ force: true })}
              disabled={running}
            >
              Force re-run
            </Button>
            {running && <CircularProgress size={24} />}
            {backgroundRunning && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Chip size="small" color="info" label="Running in background — polling status…" />
                <Button size="small" onClick={stopPolling}>
                  Stop watching
                </Button>
              </Stack>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {summary && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, maxWidth: 360 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {lastMode} summary
              </Typography>
              <SummaryRow label="Total" value={summary.total ?? 0} />
              <SummaryRow label="Processed" value={summary.processed ?? 0} />
              {summary.dryRun ? (
                <SummaryRow label="Would classify" value={summary.wouldClassify ?? 0} />
              ) : (
                <>
                  <SummaryRow label="Classified (≥1 category)" value={summary.classified ?? 0} />
                  <SummaryRow label="Filed nothing" value={summary.assignedNone ?? 0} />
                </>
              )}
              <SummaryRow label="Skipped" value={summary.skipped ?? 0} />
              <SummaryRow label="Failed" value={summary.failed ?? 0} />
              {Array.isArray(summary.failures) && summary.failures.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error">
                    Failure reasons (sample)
                  </Typography>
                  {summary.failures.map((f) => (
                    <Typography key={f.cardId} variant="caption" component="div" color="text.secondary">
                      {f.cardId}: {f.reason}
                    </Typography>
                  ))}
                </Box>
              )}
              {summary.escalations !== undefined && (
                <SummaryRow label="Escalations" value={summary.escalations} />
              )}
              {Array.isArray(summary.modelsUsed) && summary.modelsUsed.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Models used
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {summary.modelsUsed.map((m) => (
                      <Chip key={m} size="small" label={m} variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              {summary.dryRun && (
                <Chip size="small" label="Dry run — no spend" color="info" sx={{ mt: 1 }} />
              )}
            </Paper>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Status
            </Typography>
            <Button size="small" onClick={loadStats} disabled={statsLoading}>
              Refresh
            </Button>
          </Box>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip label={`Total: ${stats.total ?? 0}`} />
                <Chip label={`Categorized: ${stats.categorized ?? 0}`} color="success" />
                <Chip
                  label={`Uncategorized: ${stats.uncategorized ?? 0}`}
                  color={stats.uncategorized ? 'warning' : 'default'}
                />
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Per category
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Cards</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(stats.perCategory || {}).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            No assignments yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      Object.entries(stats.perCategory || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([id, count]) => (
                          <TableRow key={id} hover>
                            <TableCell>{id}</TableCell>
                            <TableCell align="right">{count}</TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No stats available.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Card spot-check
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inspect one card's assignments and the model that produced them — so mock-vs-real is
            visible at a glance.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="Card ID"
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 360 }}
              inputProps={{ 'aria-label': 'card id' }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={lookupCard}
              disabled={cardLoading || !cardId.trim()}
            >
              Look up
            </Button>
          </Stack>

          {cardError && <Alert severity="error">{cardError}</Alert>}

          {cardResult && (
            <Box>
              {Array.isArray(cardResult.models) && cardResult.models.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {cardResult.models.map((m) => (
                    <Chip key={m} size="small" label={m} color="primary" variant="outlined" />
                  ))}
                </Box>
              )}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Confidence</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Tax. ver.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(cardResult.categories || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            No categories assigned.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (cardResult.categories || []).map((a) => (
                        <TableRow key={`${a.id}-${a.source}`} hover>
                          <TableCell>{a.id}</TableCell>
                          <TableCell align="right">
                            {typeof a.confidence === 'number' ? a.confidence.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={a.source}
                              color={a.source === 'human' ? 'secondary' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{a.model || '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">{a.taxonomyVersion ?? '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CategorizationManager;
